import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CalendarIcon,
  MapPinIcon,
  TrophyIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FlagIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import type { ContentItem, SlideContent } from '../types/content';
import RegistrationFloater from './RegistrationFloater';

interface TopPlayer {
  position: number;
  player_name: string;
  country: string;
  total_score: number;
  category: string;
  handicap?: number;
}

export default function HeroSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [totalCapacity, setTotalCapacity] = useState(250);
  const [currentRegistrations, setCurrentRegistrations] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [slides, setSlides] = useState<SlideContent[]>([]);
  const [topPlayers, setTopPlayers] = useState<TopPlayer[]>([]);
  const [tournamentWinner, setTournamentWinner] = useState<{
    name: string;
    country: string;
    score: number;
    category: string;
    image?: string;
    handicap?: number;
  } | null>(null);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);
  const [currentTournamentName, setCurrentTournamentName] = useState<string>('');
  const [heroSlideEnabled, setHeroSlideEnabled] = useState(true);
  const [heroContent, setHeroContent] = useState<{
    title: string;
    subtitle: string;
    tagline: string;
    video: {
      type?: 'youtube' | 'video';
      url: string;
      fallbackImage: string;
      autoplay?: boolean;
      controls?: boolean;
      modestBranding?: boolean;
      showRelated?: boolean;
    };
    details: {
      date: string;
      location: string;
      prize: string;
    };
    registration: {
      openingDate: string;
      capacity: number;
      currentRegistrations: number;
    };
  }>({
    title: "IBOM OPEN 2025",
    subtitle: "Where Champions Are Made",
    tagline: "Nigeria's Premier Golf Tournament",
    video: {
      type: 'video',
      url: '',
      fallbackImage: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1920',
      autoplay: false,
      controls: true,
      modestBranding: true,
      showRelated: false
    },
    details: {
      date: 'June 7-8, 2025',
      location: 'Ibom Golf & Country Club, Uyo',
      prize: '₦50M Prize Pool'
    },
    registration: {
      openingDate: '2025-04-23T00:00:00',
      capacity: 250,
      currentRegistrations: 0
    }
  });
  

  useEffect(() => {
    fetchRegistrationData();
    fetchSlides();
    fetchHeroSlideStatus();
    fetchLeaderboardData();
  }, []);

  useEffect(() => {
    if (slides.length === 0) return;

    const totalSlides = heroSlideEnabled ? slides.length + 1 : slides.length;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        if (!heroSlideEnabled && prev === 0) {
          return 1 % totalSlides;
        }
        return (prev + 1) % totalSlides;
      });
    }, 8000);

    return () => clearInterval(interval);
  }, [slides.length, heroSlideEnabled]);

  const fetchLatestTournamentId = async (): Promise<string | null> => {
    const { data, error } = await supabase
      .from('tournaments')
      .select('id, name')
      .in('status', ['ongoing', 'completed', 'upcoming'])
      .order('start_date', { ascending: false })
      .limit(1);

    if (error || !data || data.length === 0) return null;
    setCurrentTournamentName(data[0].name);
    return data[0].id;
  };

  const fetchLeaderboardData = async () => {
    const tournamentId = await fetchLatestTournamentId();
    fetchTournamentWinner(tournamentId);
    fetchTopPlayers(tournamentId);
  };

  const fetchTournamentWinner = async (tournamentId: string | null) => {
    try {
      let query = supabase
        .from('leaderboard_view')
        .select('player_name, country, total_score, category, handicap')
        .gt('rounds_played', 0)
        .order('total_score', { ascending: true })
        .order('handicap', { ascending: true })
        .limit(1);

      if (tournamentId) {
        query = query.eq('tournament_id', tournamentId);
      }

      const { data: leaderboardData, error: leaderboardError } = await query;

      if (leaderboardError) throw leaderboardError;

      if (leaderboardData && leaderboardData.length > 0) {
        const winner = leaderboardData[0];
        setTournamentWinner({
          name: winner.player_name,
          country: winner.country,
          score: winner.total_score,
          category: winner.category,
          image: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b",
          handicap: winner.handicap
        });
      } else {
        setTournamentWinner(null);
      }
    } catch (err) {
      console.error('Error fetching tournament winner:', err);
      setTournamentWinner(null);
    }
  };

  const fetchTopPlayers = async (tournamentId: string | null) => {
    try {
      setLoadingLeaderboard(true);
      let query = supabase
        .from('leaderboard_view')
        .select('position, player_name, country, total_score, category, handicap')
        .gt('rounds_played', 0)
        .order('total_score', { ascending: true })
        .limit(7);

      if (tournamentId) {
        query = query.eq('tournament_id', tournamentId);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (data) {
        const sortedData = [...data];

        let currentPosition = 1;
        let previousScore: number | null = null;

        const dataWithCorrectPositions = sortedData.map((player, index) => {
          if (previousScore !== null && player.total_score === previousScore) {
            // Keep the same position as the previous player
          } else {
            currentPosition = index + 1;
          }

          previousScore = player.total_score;

          return {
            ...player,
            position: currentPosition
          };
        });

        setTopPlayers(dataWithCorrectPositions);
      }
    } catch (err) {
      console.error('Error fetching top players:', err);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  const fetchHeroSlideStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'leaderboard_hero_slide_enabled')
        .maybeSingle();

      if (!error && data) {
        const enabled = data.value === true || data.value === 'true';
        setHeroSlideEnabled(enabled);

        // If hero slide is disabled and we're on slide 0, move to slide 1
        if (!enabled && currentSlide === 0) {
          setCurrentSlide(1);
        }
      } else {
        setHeroSlideEnabled(true);
      }
    } catch (err) {
      console.error('Error fetching hero slide status:', err);
      setHeroSlideEnabled(true);
    }
  };

  const fetchSlides = async () => {
    try {
      setIsLoading(true);

      // Fetch hero content
      const { data: heroSections, error: heroError } = await supabase
        .from('content_sections')
        .select(`
          *,
          content_items (*)
        `)
        .eq('page', 'home')
        .eq('title', 'Hero Section')
        .eq('content_items.status', 'Published');

      if (heroError) throw heroError;

      if (heroSections && heroSections.length > 0) {
        const section = heroSections[0];
        if (section?.content_items && section.content_items.length > 0) {
          const items = section.content_items as ContentItem[];

          const titleItem = items.find(item => item.title === 'Main Title');
          const subtitleItem = items.find(item => item.title === 'Subtitle');
          const taglineItem = items.find(item => item.title === 'Tagline');
          const videoItem = items.find(item => item.title === 'Embedded Video');
          const detailsItem = items.find(item => item.title === 'Event Details');
          const registrationItem = items.find(item => item.title === 'Registration Details');

          const parsedTitle = parseContent(titleItem) || heroContent.title;
          const parsedSubtitle = parseContent(subtitleItem) || heroContent.subtitle;
          const parsedTagline = parseContent(taglineItem) || heroContent.tagline;
          const parsedVideo = parseContent(videoItem) || heroContent.video;
          const parsedDetails = parseContent(detailsItem) || heroContent.details;
          const parsedRegistration = parseContent(registrationItem) || heroContent.registration;

          setHeroContent({
            title: parsedTitle as string,
            subtitle: parsedSubtitle as string,
            tagline: parsedTagline as string,
            video: parsedVideo as typeof heroContent.video,
            details: parsedDetails as typeof heroContent.details,
            registration: parsedRegistration as typeof heroContent.registration
          });
        }
      }

      // Fetch slider content
      const { data: sliderSections, error: sliderError } = await supabase
        .from('content_sections')
        .select(`
          *,
          content_items (*)
        `)
        .eq('page', 'home')
        .eq('title', 'Hero Slider')
        .eq('content_items.status', 'Published');

      if (sliderError) throw sliderError;

      if (sliderSections && sliderSections.length > 0) {
        const section = sliderSections[0];
        if (section?.content_items && section.content_items.length > 0) {
          const slideItems = section.content_items as ContentItem[];

          const parsedSlides: SlideContent[] = [];

          for (const item of slideItems) {
            try {
              const content = typeof item.content === 'string'
                ? JSON.parse(item.content)
                : item.content;

              parsedSlides.push(content);
            } catch (e) {
              console.error('Error parsing slide content:', e);
            }
          }

          setSlides(parsedSlides);
        }
      }
    } catch (error) {
      console.error('Error fetching slides:', error);
      setSlides([]);
    } finally {
      setIsLoading(false);
    }
  };

  const parseContent = (item: ContentItem | undefined) => {
    if (!item?.content) return null;
    if (typeof item.content === 'string') {
      try {
        return JSON.parse(item.content);
      } catch {
        return item.content.replace(/^"|"$/g, '');
      }
    }
    return item.content;
  };

  const fetchRegistrationData = async () => {
    try {
      const { data: tournaments, error: tournamentError } = await supabase
        .from('tournaments')
        .select('capacity')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(1);

      if (tournamentError) throw tournamentError;

      if (tournaments && tournaments.length > 0) {
        setTotalCapacity(tournaments[0].capacity);
      }

      const { data: heroSections, error: heroError } = await supabase
        .from('content_sections')
        .select(`
          content_items (*)
        `)
        .eq('page', 'home')
        .eq('title', 'Hero Section')
        .maybeSingle();

      if (heroError) throw heroError;

      if (heroSections?.content_items) {
        const registrationItem = (heroSections.content_items as any[]).find(
          (item: any) => item.title === 'Registration Details' && item.status === 'Published'
        );

        if (registrationItem) {
          const content = typeof registrationItem.content === 'string'
            ? JSON.parse(registrationItem.content)
            : registrationItem.content;

          const openingDate = new Date(content.openingDate);
          const now = new Date();
          setIsRegistrationOpen(now >= openingDate);
        }
      }

      const { count, error: countError } = await supabase
        .from('players')
        .select('*', { count: 'exact', head: true });

      if (countError) throw countError;

      if (count !== null) {
        setCurrentRegistrations(count);
      }
    } catch (error) {
      console.error('Error fetching registration data:', error);
    }
  };

  const formatScore = (score: number): string => {
    if (score === 0) return 'E';
    if (score >= 80) return '--';
    return score > 0 ? `+${score}` : score.toString();
  };

  const getScoreColor = (score: number): string => {
    if (score < 0) return 'text-green-500';
    if (score > 0) return 'text-red-500';
    return 'text-white';
  };

  const nextSlide = () => {
    const totalSlides = heroSlideEnabled ? slides.length + 1 : slides.length;
    setCurrentSlide((prev) => {
      const next = (prev + 1) % totalSlides;
      if (!heroSlideEnabled && next === 0) {
        return 1 % totalSlides;
      }
      return next;
    });
  };

  const prevSlide = () => {
    const totalSlides = heroSlideEnabled ? slides.length + 1 : slides.length;
    setCurrentSlide((prev) => {
      const next = (prev - 1 + totalSlides) % totalSlides;
      if (!heroSlideEnabled && next === 0) {
        return totalSlides - 1;
      }
      return next;
    });
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Render hero content with leaderboard as first slide
  const renderHeroContentWithLeaderboard = () => {
    return (
      <motion.div
        key="hero-content"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -30 }}
        transition={{ duration: 0.8 }}
        className="grid lg:grid-cols-2 gap-12 items-center"
      >
        {/* Left Column */}
        <div className="text-white space-y-6">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="inline-block bg-golf-orange px-4 py-2 rounded-full text-sm font-semibold mb-6">
              {heroContent.tagline}
            </span>
            <h1 className="text-5xl lg:text-6xl font-display font-bold leading-none mb-4">
              {heroContent.title}
            </h1>
            <p className="text-xl font-light mb-8 text-golf-sand">
              {heroContent.subtitle}
            </p>
          </motion.div>

          {/* Tournament Winner Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-black/30 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/10"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-golf-orange/20 p-3 rounded-full">
                <TrophyIcon className="w-8 h-8 text-golf-orange" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{currentTournamentName ? `${currentTournamentName} Champion` : '2026 Championship Round'}</h3>
                <p className="text-golf-sand">Championship Round Results</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-golf-orange/10 flex-shrink-0">
                {tournamentWinner?.image ? (
                  <img 
                    src={tournamentWinner.image} 
                    alt={tournamentWinner.name} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = "https://images.unsplash.com/photo-1535131749006-b7f58c99034b";
                    }}
                  />
                ) : (
                  <UserIcon className="w-8 h-8 text-golf-orange m-auto mt-4" />
                )}
              </div>
              
              <div>
                <h4 className="text-2xl font-bold text-white">{tournamentWinner?.name || "Benjamin Ntuk"}</h4>
                <div className="flex items-center gap-2 text-golf-sand mt-1">
                  <div className="flex items-center gap-1">
                    <FlagIcon className="w-4 h-4" />
                    <span>{tournamentWinner?.country || "Nigeria"}</span>
                  </div>
                  {tournamentWinner?.handicap !== undefined && (
                    <div className="flex items-center gap-1 ml-2">
                      <span>•</span>
                      <span>HCP {tournamentWinner.handicap}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <div className="bg-white/10 px-3 py-1 rounded-full">
                    <span className={`font-bold ${getScoreColor(tournamentWinner?.score || -4)}`}>
                      {formatScore(tournamentWinner?.score || -4)}
                    </span>
                  </div>
                  <div className="bg-white/10 px-3 py-1 rounded-full text-white">
                    {tournamentWinner?.category || "Professional"}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 text-center">
              <Link
                to="/leaderboard"
                className="inline-block px-4 py-2 bg-golf-orange text-white rounded-full text-sm font-medium hover:bg-golf-orange/90 transition-colors"
              >
                View Full Leaderboard
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-4"
          >
            <div className="flex items-center space-x-3">
              <CalendarIcon className="w-6 h-6 text-golf-orange" />
              <span className="text-lg">{heroContent.details.date}</span>
            </div>
            <div className="flex items-center space-x-3">
              <MapPinIcon className="w-6 h-6 text-golf-orange" />
              <span className="text-lg">{heroContent.details.location}</span>
            </div>
            <div className="flex items-center space-x-3">
              <TrophyIcon className="w-6 h-6 text-golf-orange" />
              <span className="text-lg">{heroContent.details.prize}</span>
            </div>
          </motion.div>
        </div>

        {/* Right Column */}
        {/* Leaderboard Panel */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="hidden lg:block"
        >
          <div className="relative">
            <div className="absolute -inset-4 bg-golf-orange/20 rounded-full blur-xl"></div>
            <div className="relative z-10 rounded-3xl overflow-hidden bg-black/50 backdrop-blur-sm border border-white/10">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                  <TrophyIcon className="w-5 h-5 mr-2 text-golf-orange" />
                  {currentTournamentName ? `${currentTournamentName} Results` : 'Championship Results'}
                </h3>
                
                {loadingLeaderboard ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-golf-orange border-t-transparent"></div>
                  </div>
                ) : topPlayers.length > 0 ? (
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="grid grid-cols-12 text-xs text-golf-sand font-medium pb-2 border-b border-white/10">
                      <div className="col-span-1">POS</div>
                      <div className="col-span-6">PLAYER</div>
                      <div className="col-span-3">COUNTRY</div>
                      <div className="col-span-2 text-right">SCORE</div>
                    </div>
                    
                    {/* Players */}
                    {topPlayers.map((player, index) => (
                      <motion.div 
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className={`grid grid-cols-12 items-center py-2 ${
                          index === 0 ? 'bg-golf-orange/20 rounded-lg' : ''
                        }`}
                      >
                        <div className="col-span-1 text-sm font-bold text-white">
                          {player.position}
                        </div>
                        <div className="col-span-6">
                          <div className="text-sm font-medium text-white truncate">
                            {player.player_name}
                          </div>
                          <div className="text-xs text-golf-sand">
                            {player.category} {player.handicap !== undefined ? `• HCP ${player.handicap}` : ''}
                          </div>
                        </div>
                        <div className="col-span-3 text-sm text-white">
                          {player.country}
                        </div>
                        <div className={`col-span-2 text-sm font-bold text-right ${getScoreColor(player.total_score)}`}>
                          {formatScore(player.total_score)}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-golf-sand">
                    No leaderboard data available yet
                  </div>
                )}
                
                <div className="mt-4 text-center">
                  <Link 
                    to="/leaderboard" 
                    className="inline-block px-4 py-2 bg-golf-orange text-white rounded-full text-sm font-medium hover:bg-golf-orange/90 transition-colors"
                  >
                    View Full Leaderboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // Render slide content
  const renderSlideContent = (slide: SlideContent) => {
    return (
      <motion.div
        key={slide.id}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -30 }}
        transition={{ duration: 0.8 }}
        className={slide.hideImage ? "flex items-center justify-center" : "grid lg:grid-cols-2 gap-12 items-center"}
      >
        {/* Left Column */}
        <div className={`text-white space-y-6 ${slide.hideImage ? 'text-center max-w-4xl' : ''}`}>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="inline-block bg-golf-orange px-4 py-2 rounded-full text-sm font-semibold mb-6">
              {slide.subtitle}
            </span>
            <h1 className="text-5xl lg:text-6xl font-display font-bold leading-none mb-4">
              {slide.title}
            </h1>
            <p className="text-xl font-light mb-8 text-golf-sand">
              {slide.description}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link
              to={slide.buttonLink}
              className={
                slide.buttonVariant === 'primary'
                  ? 'modern-button px-8 py-4 text-white text-lg font-medium inline-block'
                  : slide.buttonVariant === 'secondary'
                  ? 'modern-button-secondary px-8 py-4 text-white text-lg font-medium inline-block'
                  : 'modern-button-outline px-8 py-4 text-lg font-medium inline-block'
              }
            >
              {slide.buttonText}
            </Link>
          </motion.div>
        </div>

        {/* Right Column - Hidden if hideImage is true */}
        {!slide.hideImage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-golf-orange/20 rounded-full blur-xl"></div>
              <div className="relative z-10 rounded-3xl overflow-hidden aspect-[4/3]">
                {slide.media && slide.media.type === 'youtube' ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${slide.media.url}?autoplay=${slide.media.autoplay ? 1 : 0}&controls=${slide.media.controls ? 1 : 0}&mute=1&origin=${window.location.origin}`}
                    title={slide.title}
                    className="w-full h-full rounded-3xl"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin"
                  />
                ) : slide.media?.type === 'video' ? (
                  <video
                    src={slide.media.url}
                    className="w-full h-full object-cover rounded-3xl"
                    autoPlay={slide.media.autoplay}
                    controls={slide.media.controls}
                    muted
                    loop
                  />
                ) : (
                  <img
                    src={slide.background.url}
                    alt={slide.title}
                    className="w-full h-full object-cover rounded-3xl"
                  />
                )}
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  };

  if (isLoading) {
    return (
      <div className="relative min-h-screen bg-black overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-golf-orange mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-black overflow-hidden">
      {/* Slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
        >
          {/* Background Image */}
          <div className="absolute inset-0 w-full h-full opacity-60 bg-cover bg-center"
            style={{
              backgroundImage: (currentSlide === 0 && heroSlideEnabled)
                ? `url(${heroContent.video.fallbackImage})`
                : (heroSlideEnabled
                    ? (slides[currentSlide - 1]?.background?.url
                        ? `url(${slides[currentSlide - 1].background.url})`
                        : `url(${heroContent.video.fallbackImage})`)
                    : (slides[currentSlide]?.background?.url
                        ? `url(${slides[currentSlide].background.url})`
                        : `url(${heroContent.video.fallbackImage})`)
                  )
            }}
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/70" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative h-full">
        <div className="container mx-auto px-4 h-full flex items-center">
          <div className="max-w-7xl mx-auto w-full pt-48">
            <AnimatePresence mode="wait">
              {currentSlide === 0 && heroSlideEnabled
                ? renderHeroContentWithLeaderboard()
                : (heroSlideEnabled && slides[currentSlide - 1]
                    ? renderSlideContent(slides[currentSlide - 1])
                    : (!heroSlideEnabled && slides[currentSlide]
                        ? renderSlideContent(slides[currentSlide])
                        : renderHeroContentWithLeaderboard())
                  )
              }
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {slides.length > 0 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors z-10"
            aria-label="Go to previous slide"
            type="button"
          >
            <ChevronLeftIcon className="w-6 h-6" aria-hidden="true" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors z-10"
            aria-label="Go to next slide"
            type="button"
          >
            <ChevronRightIcon className="w-6 h-6" aria-hidden="true" />
          </button>
        </>
      )}

      {/* Registration Floater */}
      <RegistrationFloater />

      {/* Slide Indicators */}
      {slides.length > 0 && (
        <div className="absolute bottom-8 left-0 right-0 flex justify-center space-x-2 z-10" role="tablist" aria-label="Slide navigation">
          {[...Array(heroSlideEnabled ? slides.length + 1 : slides.length)].map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentSlide ? 'bg-golf-orange' : 'bg-white/50 hover:bg-white/80'
              }`}
              role="tab"
              aria-label={`${index === 0 && heroSlideEnabled ? 'Tournament overview' : `Slide ${heroSlideEnabled ? index : index + 1}`}`}
              aria-selected={index === currentSlide}
              aria-controls={`slide-${index}`}
              type="button"
            />
          ))}
        </div>
      )}
    </div>
  );
}